import React, { useState, useEffect, useCallback, useRef } from 'react';
import { documentsApi } from '../services/api';
import './RecherchePdf.css';

// Module « Documents / Recherche PDF » (RAG).
// Niveau 1 : liste des projets documentaires. Niveau 2 : espace projet
// (import multi-PDF + statut d'indexation + chat avec citations).

const STATUTS = {
  en_attente: { label: 'En attente', cls: 'info',    spin: true,  icon: '⏳' },
  extraction: { label: 'Extraction…', cls: 'info',   spin: true,  icon: '📥' },
  ocr:        { label: 'OCR…',        cls: 'info',    spin: true,  icon: '🔍' },
  indexe:     { label: 'Indexé',      cls: 'success', spin: false, icon: '✅' },
  erreur:     { label: 'Erreur',      cls: 'error',   spin: false, icon: '⚠️' },
};
const isTerminal = (s) => s === 'indexe' || s === 'erreur';

function relativeDate(d) {
  if (!d) return '';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 2592000) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(d).toLocaleDateString('fr-FR');
}
function humanSize(bytes) {
  if (!bytes) return '';
  const u = ['o', 'Ko', 'Mo', 'Go'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function RecherchePdf({ user, setHeaderAction, editMode }) {
  const canDelete = editMode && user?.role === 'super-admin';

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // projet ouvert

  // Modale création / renommage
  const [projectModal, setProjectModal] = useState(null); // {mode:'create'|'edit', nom, description, id}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadProjects = useCallback(async () => {
    try {
      const res = await documentsApi.listProjects();
      setProjects(res.data.projects || []);
    } catch (e) {
      setError("Impossible de charger les projets documentaires.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Bouton d'action dans le header (dépend de la vue courante)
  useEffect(() => {
    if (!setHeaderAction) return;
    if (selected) {
      setHeaderAction({
        primary: { label: '+ Importer des PDF', onClick: () => window.dispatchEvent(new CustomEvent('rp-trigger-upload')) },
        secondary: [{ label: '← Tous les projets', onClick: () => setSelected(null) }],
      });
    } else {
      setHeaderAction({ primary: { label: '+ Nouveau projet', onClick: () => setProjectModal({ mode: 'create', nom: '', description: '' }) } });
    }
  }, [selected, setHeaderAction]);

  const saveProject = async () => {
    if (!projectModal.nom.trim()) { setError('Le nom du projet est requis.'); return; }
    setSaving(true); setError('');
    try {
      if (projectModal.mode === 'create') {
        await documentsApi.createProject({ nom: projectModal.nom, description: projectModal.description });
      } else {
        await documentsApi.updateProject(projectModal.id, { nom: projectModal.nom, description: projectModal.description });
      }
      setProjectModal(null);
      await loadProjects();
    } catch (e) {
      setError(e.response?.data?.error || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Supprimer le projet « ${p.nom} » et tous ses documents ? Cette action est irréversible.`)) return;
    try {
      await documentsApi.deleteProject(p.id);
      await loadProjects();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  if (selected) {
    return (
      <ProjectWorkspace
        project={selected}
        user={user}
        canDelete={canDelete}
        onBack={() => { setSelected(null); loadProjects(); }}
      />
    );
  }

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className="active">📂 Projets documentaires</button>
      </div>

      {loading ? (
        <div className="rp-empty"><div className="rp-spinner" /> Chargement des projets…</div>
      ) : projects.length === 0 ? (
        <div className="rp-empty">
          <div className="rp-empty-icon">📄</div>
          <h3>Aucun projet documentaire</h3>
          <p>Créez un projet (ex. « Homologation nouvelle voiture »), importez vos PDF, puis interrogez-les par chat pour retrouver une information précise avec ses sources.</p>
          <button className="btn-primary" onClick={() => setProjectModal({ mode: 'create', nom: '', description: '' })}>+ Créer un projet</button>
        </div>
      ) : (
        <div className="rp-project-grid">
          {projects.map((p) => (
            <div key={p.id} className="rp-project-card" onClick={() => setSelected(p)}>
              <div className="rp-card-head">
                <h3>{p.nom}</h3>
                <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-icon" title="Renommer" onClick={() => setProjectModal({ mode: 'edit', id: p.id, nom: p.nom, description: p.description || '' })}>✏️</button>
                  {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => deleteProject(p)}>🗑️</button>}
                </div>
              </div>
              {p.description && <p className="rp-card-desc">{p.description}</p>}
              <div className="rp-card-stats">
                <span className="rp-chip">📄 {p.nb_documents} doc{p.nb_documents > 1 ? 's' : ''}</span>
                {p.nb_indexes > 0 && <span className="rp-chip status-success">✅ {p.nb_indexes} indexé{p.nb_indexes > 1 ? 's' : ''}</span>}
                {p.nb_en_cours > 0 && <span className="rp-chip status-info">⏳ {p.nb_en_cours} en cours</span>}
                {p.nb_erreurs > 0 && <span className="rp-chip status-error">⚠️ {p.nb_erreurs} erreur{p.nb_erreurs > 1 ? 's' : ''}</span>}
              </div>
              <div className="rp-card-foot">
                {user?.role === 'super-admin' && p.owner_prenom && <span>{p.owner_prenom} {p.owner_nom} · </span>}
                <span>{relativeDate(p.updated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {projectModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{projectModal.mode === 'create' ? 'Nouveau projet documentaire' : 'Renommer le projet'}</h2>
              <button className="modal-close" onClick={() => setProjectModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="rp-banner status-error">{error}</div>}
              <label className="rp-field">
                <span>Nom du projet *</span>
                <input autoFocus value={projectModal.nom} onChange={(e) => setProjectModal({ ...projectModal, nom: e.target.value })} placeholder="Ex. Homologation nouvelle voiture" />
              </label>
              <label className="rp-field">
                <span>Description (optionnel)</span>
                <textarea rows={3} value={projectModal.description} onChange={(e) => setProjectModal({ ...projectModal, description: e.target.value })} placeholder="À quoi sert ce projet ?" />
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setProjectModal(null)}>Annuler</button>
              <button className="btn-primary" onClick={saveProject} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Espace projet : documents + chat ─────────────────────────────────────────
function ProjectWorkspace({ project, user, canDelete }) {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploads, setUploads] = useState([]); // {name, state:'upload'|'error', error?}
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null); // {url, nom, page}
  const [renameDoc, setRenameDoc] = useState(null); // {id, nom, libelle}
  const [renameSaving, setRenameSaving] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadDocs = useCallback(async () => {
    try {
      const res = await documentsApi.listDocuments(project.id);
      setDocuments(res.data.documents || []);
    } catch (e) { /* silencieux : la vue reste utilisable */ }
    finally { setLoadingDocs(false); }
  }, [project.id]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Polling tant qu'un document est en cours d'indexation
  useEffect(() => {
    const pending = documents.some((d) => !isTerminal(d.statut));
    if (pending && !pollRef.current) {
      pollRef.current = setInterval(loadDocs, 3000);
    } else if (!pending && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !documents.some((d) => !isTerminal(d.statut))) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [documents, loadDocs]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Le bouton header « + Importer des PDF » déclenche l'input fichier
  useEffect(() => {
    const h = () => fileInputRef.current?.click();
    window.addEventListener('rp-trigger-upload', h);
    return () => window.removeEventListener('rp-trigger-upload', h);
  }, []);

  const uploadOne = useCallback(async (file) => {
    const name = file.name;
    setUploads((u) => [...u, { name, state: 'upload' }]);
    try {
      // 1) URL signée → 2) PUT direct S3 → 3) confirm (déclenche l'ingestion)
      const { data } = await documentsApi.presign(project.id, { filename: name, contentType: 'application/pdf' });
      const put = await fetch(data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: file });
      if (!put.ok) throw new Error('upload S3');
      await documentsApi.confirm(project.id, { key: data.key, filename: name, contentType: 'application/pdf', size: file.size });
      setUploads((u) => u.filter((x) => x.name !== name));
      await loadDocs();
    } catch (e) {
      // Repli : upload multipart via le backend si le PUT direct échoue
      try {
        const form = new FormData();
        form.append('file', file);
        await documentsApi.uploadFile(project.id, form);
        setUploads((u) => u.filter((x) => x.name !== name));
        await loadDocs();
      } catch (e2) {
        setUploads((u) => u.map((x) => x.name === name ? { ...x, state: 'error', error: "Échec de l'upload" } : x));
      }
    }
  }, [project.id, loadDocs]);

  const handleFiles = useCallback((fileList) => {
    const files = Array.from(fileList).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    const rejected = fileList.length - files.length;
    if (rejected > 0) setUploads((u) => [...u, { name: `${rejected} fichier(s) ignoré(s)`, state: 'error', error: 'PDF uniquement' }]);
    files.forEach(uploadOne);
  }, [uploadOne]);

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.nom} » ?`)) return;
    try { await documentsApi.deleteDocument(project.id, doc.id); await loadDocs(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur lors de la suppression.'); }
  };
  const reindex = async (doc) => {
    try { await documentsApi.reindex(project.id, doc.id); await loadDocs(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur lors de la ré-indexation.'); }
  };
  const saveRename = async () => {
    setRenameSaving(true);
    try {
      await documentsApi.renameDocument(project.id, renameDoc.id, { libelle: renameDoc.libelle });
      setRenameDoc(null);
      await loadDocs();
    } catch (e) { alert(e.response?.data?.error || 'Erreur lors du renommage.'); }
    finally { setRenameSaving(false); }
  };

  const openPreview = async (docId, nom, page) => {
    try {
      const { data } = await documentsApi.fileUrl(project.id, docId);
      setPreview({ url: data.url, nom, page });
    } catch (e) { alert("Aperçu indisponible pour ce document."); }
  };

  const nbIndexed = documents.filter((d) => d.statut === 'indexe').length;

  return (
    <div className="page-container rp-workspace">
      <div className="rp-project-title">
        <h2>{project.nom}</h2>
        {project.description && <p>{project.description}</p>}
      </div>

      <div className="rp-panes">
        {/* Colonne documents */}
        <div className="rp-docs-pane">
          <input ref={fileInputRef} type="file" accept="application/pdf" multiple hidden onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
          <div
            className={`rp-dropzone ${dragOver ? 'over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="rp-dropzone-icon">⬆️</div>
            <div><strong>Glissez vos PDF ici</strong> ou cliquez pour parcourir</div>
            <div className="rp-dropzone-hint">PDF uniquement · multilingue (FR/EN/ZH…) · plusieurs fichiers acceptés</div>
          </div>

          {loadingDocs ? (
            <div className="rp-empty small"><div className="rp-spinner" /> Chargement…</div>
          ) : (documents.length === 0 && uploads.length === 0) ? (
            <div className="rp-empty small"><p>Aucun document. Importez vos premiers PDF pour lancer l'indexation.</p></div>
          ) : (
            <ul className="rp-doc-list">
              {uploads.map((up, i) => (
                <li key={`up-${i}`} className="rp-doc-row">
                  <span className="rp-doc-name">📄 {up.name}</span>
                  {up.state === 'error'
                    ? <span className="rp-badge status-error">⚠️ {up.error}</span>
                    : <span className="rp-badge status-info"><span className="rp-spinner tiny" /> Envoi…</span>}
                </li>
              ))}
              {documents.map((d) => {
                const st = STATUTS[d.statut] || STATUTS.en_attente;
                return (
                  <li key={d.id} className="rp-doc-row">
                    <div className="rp-doc-main">
                      <span className="rp-doc-name" title={d.libelle || d.nom}>📄 {d.libelle || d.nom}</span>
                      {d.libelle && <span className="rp-doc-orig" title={d.nom}>{d.nom}</span>}
                      <span className="rp-doc-meta">
                        {humanSize(d.taille_octets)}
                        {d.statut === 'indexe' && d.nb_pages ? ` · ${d.nb_pages} p. · ${d.nb_chunks} passages` : ''}
                      </span>
                      {d.statut === 'erreur' && d.erreur && <span className="rp-doc-error">{d.erreur}</span>}
                    </div>
                    <div className="rp-doc-actions">
                      <span className={`rp-badge status-${st.cls}`}>
                        {st.spin ? <span className="rp-spinner tiny" /> : st.icon} {st.label}
                      </span>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Nom personnalisé" onClick={() => setRenameDoc({ id: d.id, nom: d.nom, libelle: d.libelle || '' })}>✏️</button>
                        <button className="btn-icon" title="Aperçu du PDF" onClick={() => openPreview(d.id, d.libelle || d.nom, 1)}>👁️</button>
                        {d.statut === 'erreur' && <button className="btn-icon" title="Réessayer l'indexation" onClick={() => reindex(d)}>🔄</button>}
                        {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => deleteDoc(d)}>🗑️</button>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Colonne chat */}
        <ChatPanel project={project} nbIndexed={nbIndexed} onOpenSource={openPreview} />
      </div>

      {renameDoc && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nom personnalisé</h2>
              <button className="modal-close" onClick={() => setRenameDoc(null)}>✕</button>
            </div>
            <div className="modal-body">
              <label className="rp-field">
                <span>Fichier original</span>
                <input value={renameDoc.nom} readOnly className="rp-readonly" />
              </label>
              <label className="rp-field">
                <span>Nom personnalisé (optionnel)</span>
                <input autoFocus value={renameDoc.libelle}
                  onChange={(e) => setRenameDoc({ ...renameDoc, libelle: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
                  placeholder="Ex. Manuel d'homologation — chapitre 3" />
              </label>
              <p className="rp-modal-hint">Le nom de fichier original est conservé. Le nom personnalisé s'affiche en priorité, y compris dans les sources du chat. Videz le champ pour revenir au nom d'origine.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setRenameDoc(null)}>Annuler</button>
              <button className="btn-primary" onClick={saveRename} disabled={renameSaving}>{renameSaving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{preview.nom}{preview.page ? ` — page ${preview.page}` : ''}</h2>
              <button className="modal-close" onClick={() => setPreview(null)}>✕</button>
            </div>
            <div className="modal-body rp-preview-body">
              <iframe title="Aperçu PDF" src={`${preview.url}#page=${preview.page || 1}`} className="rp-preview-frame" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panneau de chat ──────────────────────────────────────────────────────────
function ChatPanel({ project, nbIndexed, onOpenSource }) {
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content, sources?, empty?}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Historique du projet
    documentsApi.conversations(project.id).then((res) => {
      const hist = [];
      for (const c of res.data.conversations || []) {
        hist.push({ role: 'user', content: c.question });
        hist.push({ role: 'assistant', content: c.answer, sources: c.sources || [], empty: !c.answer });
      }
      setMessages(hist);
    }).catch(() => {});
  }, [project.id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, sending]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setSending(true);
    try {
      const { data } = await documentsApi.chat(project.id, q);
      setMessages((m) => [...m, { role: 'assistant', content: data.answer, sources: data.sources || [], empty: !data.answer, message: data.message }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: null, error: e.response?.data?.error || 'Erreur lors de la génération de la réponse.' }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rp-chat-pane">
      <div className="rp-chat-messages" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="rp-empty small">
            <div className="rp-empty-icon">💬</div>
            <p>Posez une question sur les documents de ce projet.<br />Ex. « Quelle est la taille des pneus ? », « Référence de la pièce X ? »</p>
            {nbIndexed === 0 && <p className="rp-hint">⏳ Aucun document indexé pour l'instant — importez des PDF et attendez la fin de l'indexation.</p>}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`rp-msg ${m.role}`}>
            <div className="rp-msg-bubble">
              {m.error ? <span className="rp-msg-error">⚠️ {m.error}</span>
                : m.empty ? <em>{m.message || "Je n'ai pas trouvé cette information dans les documents du projet."}</em>
                : <span className="rp-msg-text">{m.content}</span>}
              {m.sources && m.sources.length > 0 && (
                <div className="rp-sources">
                  <div className="rp-sources-title">Sources</div>
                  {m.sources.map((s, j) => (
                    <button key={j} className="rp-source" onClick={() => onOpenSource(s.document_id, s.nom, s.page)} title="Ouvrir le PDF à cette page">
                      <div className="rp-source-head">
                        <span className="rp-source-doc">📄 {s.nom}</span>
                        <span className="rp-source-page">p. {s.page}</span>
                        {typeof s.score === 'number' && <span className="rp-source-score">{Math.round(s.score * 100)}%</span>}
                      </div>
                      <div className="rp-source-extract">« {s.extrait} »</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="rp-msg assistant"><div className="rp-msg-bubble"><span className="rp-typing"><i /><i /><i /></span></div></div>}
      </div>

      <div className="rp-chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={nbIndexed === 0 ? "Importez et indexez des PDF pour interroger le projet…" : "Posez votre question… (Entrée pour envoyer)"}
          rows={2}
          disabled={sending}
        />
        <button className="btn-primary" onClick={send} disabled={sending || !input.trim()}>{sending ? '…' : 'Envoyer'}</button>
      </div>
    </div>
  );
}
